import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { teamAPI } from '../services/api';

const parseSettings = (member) => {
  let settings = member?.settings;
  if (typeof settings === 'string') {
    try { settings = JSON.parse(settings); } catch { settings = null; }
  }
  return settings || {};
};

export const isTrainee = (member) => !!parseSettings(member).is_trainee;

// Trainee pay summary: "$18.00/hr", "30% of job" or "$40.00/job".
export const traineePayLabel = (member) => {
  const s = parseSettings(member);
  if (s.pay_type === 'fixed') {
    const amt = parseFloat(s.fixed_pay_per_job);
    if (Number.isFinite(amt) && amt > 0) return `$${amt.toFixed(2)}/job`;
  }
  const pct = parseFloat(member?.commission_percentage);
  if (s.pay_type === 'percent' && Number.isFinite(pct) && pct > 0) return `${pct}% of job`;
  const rate = parseFloat(member?.hourly_rate);
  return Number.isFinite(rate) && rate > 0 ? `$${rate.toFixed(2)}/hr` : '';
};

export const traineeLabel = (member) => {
  const pay = traineePayLabel(member);
  return pay ? `Trainee · ${pay}` : 'Trainee';
};

const PAY_TYPES = [
  { id: 'hourly', label: '$/hr', placeholder: 'Rate/hr', prefix: '$' },
  { id: 'percent', label: '%', placeholder: '% of job', suffix: '%' },
  { id: 'fixed', label: '$/job', placeholder: 'Per job', prefix: '$' },
];

// "+ Add trainee" toggle that expands to Name + pay (hourly, % of the job,
// or a fixed price per job). Creates the trainee as a team member and hands
// the new row to onAdded.
export default function AddTraineeForm({ onAdded, className = '' }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [payType, setPayType] = useState('hourly');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setOpen(false);
    setName('');
    setPayType('hourly');
    setAmount('');
    setError('');
  };

  const type = PAY_TYPES.find((t) => t.id === payType);

  const handleAdd = async () => {
    const trimmed = name.trim();
    const value = parseFloat(amount);
    if (!trimmed) return setError('Enter a name');
    if (!Number.isFinite(value) || value <= 0) {
      return setError(payType === 'percent' ? 'Enter a percentage' : payType === 'fixed' ? 'Enter a price per job' : 'Enter an hourly rate');
    }
    if (payType === 'percent' && value > 100) return setError('Percentage can be at most 100');

    setSaving(true);
    setError('');
    try {
      const { teamMember } = await teamAPI.createTrainee({ name: trimmed, payType, amount: value });
      await onAdded?.(teamMember);
      reset();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not add trainee');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 text-sm font-medium text-[var(--sf-blue-500)] hover:underline ${className}`}
      >
        <Plus className="w-4 h-4" />
        Add trainee
      </button>
    );
  }

  return (
    <div className={`rounded-lg border border-[var(--sf-border-light)] p-3 space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--sf-text-primary)]">Add trainee</span>
        <button
          type="button"
          onClick={reset}
          className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-secondary)]"
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        autoFocus
        className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]"
      />
      <div className="flex gap-2">
        <div className="flex rounded-lg border border-[var(--sf-border-light)] overflow-hidden flex-shrink-0" role="radiogroup" aria-label="Pay type">
          {PAY_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={payType === t.id}
              onClick={() => setPayType(t.id)}
              className={`px-2.5 py-2 text-xs font-semibold transition-colors ${
                payType === t.id
                  ? 'bg-[var(--sf-blue-500)] text-white'
                  : 'text-[var(--sf-text-secondary)] hover:bg-[var(--sf-bg-hover)]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-0">
          {type.prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--sf-text-muted)]">{type.prefix}</span>
          )}
          <input
            type="number"
            min="0"
            max={payType === 'percent' ? 100 : undefined}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder={type.placeholder}
            className={`w-full ${type.prefix ? 'pl-6' : 'pl-3'} ${type.suffix ? 'pr-7' : 'pr-2'} py-2 text-sm border border-[var(--sf-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]`}
          />
          {type.suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--sf-text-muted)]">{type.suffix}</span>
          )}
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="button"
        onClick={handleAdd}
        disabled={saving}
        className="w-full px-3 py-2 text-sm font-medium text-white bg-[var(--sf-blue-500)] rounded-lg hover:opacity-90 disabled:opacity-50"
      >
        {saving ? 'Adding...' : 'Add'}
      </button>
    </div>
  );
}
